import React, { useContext, useState, useCallback, createContext, useEffect, useMemo, useDebugValue, useRef } from "react";
import last from 'lodash/last'
import isString from 'lodash/isString'
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { getEnvVar } from 'reacticoon/environment'
import { getJwtToken } from 'modules/auth/config'

const cache = {}

function cacheMessages(subscriptionType, messages, getId) {
	const current = cache[subscriptionType] || []

	if (!current) {
		cache[subscriptionType] = messages
	} else {
		const currentIds = cache[subscriptionType].map(m => getId(m))
		const newIds = messages.map(m => getId(m))
		const intersection = newIds.filter(x => currentIds.includes(x))

		if (currentIds.length !== newIds.length || intersection.length > 0) {
			console.log(`intersection: ${subscriptionType}`, currentIds, newIds)
			cache[subscriptionType] = messages
		}
	}


	return cache[subscriptionType]
}

function updateMessageHistory(prev, lastMessage) {
	let result = [...prev]

	const doNotKeepHistory = [
		'SUBSCRIBE',
		'UNSUBSCRIBE',
		'LOGIN',
	]

	if (doNotKeepHistory.includes(lastMessage.type)) {
		// do not concat, do not keep in history
		return result
	}

	// TODO: clean older messages, REPORTS: keep last one
	if (lastMessage.type === 'BROADCAST') {

		if (
			lastMessage.channel.startsWith("TICKET/") // TICKET/42
			|| lastMessage.channel.startsWith("TICKET_TYPING/") // TICKET_TYPING/42
			|| lastMessage.channel.startsWith("PLAYER_COUNT")
			|| lastMessage.channel === "PLAYER_LIST" // Keep only last PLAYER_LIST message
			|| lastMessage.channel === "PLAYER_LIST_UPDATE" // Keep only last differential update
		) { 
			result = result.filter(r => r.channel !== lastMessage.channel)
		} else {
			switch (lastMessage.channel) {
				// keep last one:
				case 'REPORTS':
				case 'TICKET':
					result = result.filter(r => r.channel !== lastMessage.channel)
					break

				default:
			}
		}
	}

	result = result.concat(lastMessage)

	return result
}

let ContextWebsocket;
let { Provider } = (ContextWebsocket = createContext());

ContextWebsocket.displayName = 'ContextWebsocket'

export const WebsocketProvider = ({ children }) => {
	const [messageHistory, setMessageHistory] = useState([]);
	const [subscriptions, setSubscriptions] = useState({})
	const [loggedIn, setLoggedIn] = useState(false)
	const isMountedRef = useRef(true)

	// Cleanup au démontage
	useEffect(() => {
		return () => {
			isMountedRef.current = false
		}
	}, [])

	const { sendJsonMessage: _sendMessage, lastMessage: lastMessageData, readyState } = useWebSocket(
		getEnvVar('WEBSOCKET_URL'),
		{
			onOpen: () => {
				// console.log('websocket opened')
			},
			onClose: () => {
				// console.log('websocket closed')
				setSubscriptions([])
				setLoggedIn(false)
			},
			//Will attempt to reconnect on all close events, such as server shutting down
			shouldReconnect: (closeEvent) => true,
		}
	);

	function onReceive(message) {
		if (!isMountedRef.current) return // Ne pas mettre à jour le state si démonté
		
		if (message.type === 'LOGIN') {
			if (message.status === 200) {
				setLoggedIn(true)
			} else {
				setLoggedIn(false)
			}
		}
		
		// trick just in case we missed a SUBSCRIBE
		if (message.type === "BROADCAST" && !subscriptions[message.channel]) {
			setSubscriptions({ ...subscriptions, [message.channel]: { connected: true } })
		}
		if (message.type === "UNSUBSCRIBE") {
			setSubscriptions({ ...subscriptions, [message.channel]: { connected: false } })
			cache[message.channel] = []
		}
		if (message.type === "SUBSCRIBE") {
			setSubscriptions({ ...subscriptions, [message.channel]: { connected: true } })
			cache[message.channel] = []
		}
	}

	const sendMessage = useCallback((command, payload) => {
		_sendMessage(
			{
				command,
				payload
			}
		)
	}, [_sendMessage])

	const login = useCallback(() => {
		const jwt = getJwtToken()
		
		// Le backend s'attend à recevoir "Bearer TOKEN" et fait jwt.split(' ')[1]
		// Donc on doit s'assurer que le JWT a le préfixe "Bearer "
		const jwtWithBearer = jwt && jwt.startsWith('Bearer ') ? jwt : `Bearer ${jwt}`
		
		sendMessage('LOGIN', {
			jwt: jwtWithBearer
		})
	}, [sendMessage]);

	const subscribe = useCallback((type) => {
		// console.info(`SUBSCRIBE ${type}`)
		// if (!subscriptions[type]) {
			sendMessage('SUBSCRIBE', {
				type,
			})
		// }
	}, [sendMessage]);

	const unsubscribe = useCallback((type) => {
		// console.info(`UNSUBSCRIBE ${type}`)
		// if (subscriptions[type] === true) {
			sendMessage('UNSUBSCRIBE', {
				type,
			})
		// }
	}, [sendMessage ]);

	useEffect(() => {
		if (lastMessageData !== null) {
			if (isString(lastMessageData.data)) { // else is a binary, API didn't correctly sent a message, ignore it
				const lastMessage = JSON.parse(lastMessageData.data) 

				if (isMountedRef.current) {
					setMessageHistory(prev => updateMessageHistory(
						prev,
						lastMessage,
					));

					onReceive(lastMessage)
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lastMessageData, setMessageHistory]);

	useEffect(() => {
		if (readyState === ReadyState.OPEN && isMountedRef.current) {
			login()
		}
	}, [login, readyState])

	const connectionStatus = {
		[ReadyState.CONNECTING]: 'Connecting',
		[ReadyState.OPEN]: 'Open',
		[ReadyState.CLOSING]: 'Closing',
		[ReadyState.CLOSED]: 'Closed',
		[ReadyState.UNINSTANTIATED]: 'Uninstantiated',
	}[readyState];

	const value = {
		loggedIn,
		connectionStatus,

		messageHistory,
		// lastMessage: last(messageHistory),
		sendMessage,
		readyState,
		login,
		subscribe,
		unsubscribe,
		subscriptions,
	}

	return (
		<Provider value={value}>
			{children}
		</Provider>
	);
};

export default function useWebsocket() {
	const context = useContext(ContextWebsocket)
	if (context === undefined) {
		throw new Error('useWebsocket must be used within a WebsocketProvider')
	}

	// console.info(context?.character)
	return context
}

/**
 * Hook pour écouter les événements websocket de type EVENT (push depuis le serveur sans abonnement)
 * Utilisé pour les notifications temps réel comme les détections de fusillades
 */
export function useWebsocketEvent(eventType, options = {}) {
	const { messageHistory } = useWebsocket()
	const { onMessage } = options
	const onMessageRef = useRef(onMessage)
	const lastProcessedRef = useRef(null)

	useDebugValue("useWebsocketEvent: " + eventType)

	// Toujours garder la dernière version du callback
	useEffect(() => {
		onMessageRef.current = onMessage
	}, [onMessage])

	// Filtrer les messages de type EVENT avec le bon eventType
	const messages = useMemo(() => {
		return messageHistory.filter(m => m.type === 'EVENT' && m.eventType === eventType)
	}, [messageHistory, eventType])

	const lastMessage = useMemo(() => last(messages), [messages])

	// Appeler onMessage seulement quand un NOUVEAU message arrive
	useEffect(() => {
		if (lastMessage && onMessageRef.current) {
			// Identifier unique du message pour éviter de traiter le même message plusieurs fois
			const messageId = JSON.stringify(lastMessage)
			if (lastProcessedRef.current !== messageId) {
				lastProcessedRef.current = messageId
				onMessageRef.current(lastMessage.data)
			}
		}
	}, [lastMessage])

	return {
		lastMessage: lastMessage?.data,
		messages: messages.map(m => m.data),
	}
}

export function useWebsocketChannel(subscriptionType, options = {}) {
	const { 
		loggedIn, 
		readyState, 
		sendMessage: _sendMessage, 
		subscribe, 
		subscriptions, 
		unsubscribe, 
		messageHistory,
	} = useWebsocket()

	const { 
		withCache = false,
		getId = null,
		onMessage,
	} = options

	useDebugValue("useWebsocketChannel: " + subscriptionType)

	const sendMessage = useCallback((payload) => {
		_sendMessage(subscriptionType, payload)
	}, [_sendMessage, subscriptionType])

	useEffect(() => {
		if (loggedIn) {
			subscribe(subscriptionType)
			
			return () => {
				unsubscribe(subscriptionType)
			}
		}
	}, [loggedIn, subscribe, unsubscribe, subscriptionType])

	// TODO: this is recomputed each time we receive a new websocket message
	const messages = useMemo(() => {
		const messages = messageHistory.filter(m => m.type === 'BROADCAST' && m.channel === subscriptionType)
		if (withCache) {
			return cacheMessages(subscriptionType, messages, getId)
		}
		return messages
	}, [messageHistory, subscriptionType, withCache, getId])

	const lastMessage = useMemo(() => last(messages), [ messages ])

	useEffect(() => {
		if (lastMessage && onMessage) {
			onMessage(lastMessage)
		}
	}, [lastMessage, onMessage])

	return {
		loggedIn,
		readyState,
		sendMessage,
		subscribed: !!subscriptions[subscriptionType],
		lastMessage,
		messages,
	}
}